import React from 'react'
import { CreditCard, Crown, Clock, AlertTriangle, TrendingUp, Users, Plus, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, X } from 'lucide-react'

export default function SubscriptionsTab({ 
  subsData, 
  subsLoading, 
  subsSummary,
  subPage,
  setSubPage,
  subPlan,
  setSubPlan,
  subActionLoading,
  handleUpgrade,
  handleDowngrade,
  showExtendModal,
  setShowExtendModal,
  selectedTeam,
  setSelectedTeam,
  extendDays,
  setExtendDays,
  handleExtendTrial
}) {
  const formatCurrency = (cents) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format((cents || 0) / 100)
  const formatDate = (dateStr) => dateStr ? new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-'
  
  const getPlanBadge = (plan) => {
    switch (plan) {
      case 'premium': return <span className="px-2 py-1 bg-green-500/100/20 text-green-400 rounded-full text-xs flex items-center gap-1"><Crown className="w-3 h-3" />Premium</span>
      case 'trial': return <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-xs flex items-center gap-1"><Clock className="w-3 h-3" />Trial</span>
      default: return <span className="px-2 py-1 bg-zinc-800 text-zinc-200 rounded-full text-xs">Free</span>
    }
  }
  
  const isTrialExpiringSoon = (trialEndsAt) => {
    if (!trialEndsAt) return false
    const daysLeft = Math.ceil((new Date(trialEndsAt) - new Date()) / (1000 * 60 * 60 * 24))
    return daysLeft <= 7 && daysLeft > 0
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-zinc-800 rounded-xl p-5 border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-500/100/20 flex items-center justify-center">
              <Crown className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <p className="text-sm text-zinc-400">Premium</p>
              <p className="text-xl font-bold">{subsSummary?.plan_counts?.premium || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-zinc-800 rounded-xl p-5 border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
              <Clock className="w-5 h-5 text-yellow-400" />
            </div>
            <div>
              <p className="text-sm text-zinc-400">Trials</p>
              <p className="text-xl font-bold">{subsSummary?.plan_counts?.trial || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-zinc-800 rounded-xl p-5 border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-orange-400" />
            </div>
            <div>
              <p className="text-sm text-zinc-400">Expiring Soon</p>
              <p className="text-xl font-bold">{subsSummary?.trials_expiring_soon || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-zinc-800 rounded-xl p-5 border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-zinc-400">MRR (Combined)</p>
              <p className="text-xl font-bold">${subsSummary?.mrr_dollars?.toFixed(2) || formatCurrency(subsSummary?.mrr_cents || 0)}</p>
              {subsSummary?.apple_subs > 0 && (
                <p className="text-xs text-zinc-500 mt-1">
                  Stripe ${subsSummary?.stripe_mrr?.toFixed(2)} · Apple ${subsSummary?.apple_mrr?.toFixed(2)} ({subsSummary?.apple_subs} sub{subsSummary?.apple_subs !== 1 ? 's' : ''})
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Filter */}
      <div className="flex gap-4">
        <select
          value={subPlan}
          onChange={(e) => { setSubPlan(e.target.value); setSubPage(1) }}
          className="px-4 py-2 border rounded-lg"
        >
          <option value="">All Plans</option>
          <option value="free">Free</option>
          <option value="trial">Trial</option>
          <option value="premium">Premium</option>
        </select>
      </div>

      {/* Subscriptions Table */}
      <div className="bg-zinc-800 rounded-2xl border overflow-hidden">
        {subsLoading ? (
          <div className="p-12 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div></div>
        ) : (
          <>
            <table className="w-full">
              <thead className="bg-zinc-900 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Team</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Owner</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Plan</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Members</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Trial Ends</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {subsData?.subscriptions?.map((sub) => (
                  <tr key={sub.team_id} className="hover:bg-zinc-900">
                    <td className="px-6 py-4">
                      <p className="font-medium">{sub.name}</p>
                      <p className="text-xs text-zinc-400">{sub.sport}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-medium">{sub.owner_name}</p>
                      <p className="text-xs text-zinc-400">{sub.owner_email}</p>
                    </td>
                    <td className="px-6 py-4">{getPlanBadge(sub.plan)}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-zinc-500" />
                        <span>{sub.member_count}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {sub.trial_ends_at ? (
                        <span className={isTrialExpiringSoon(sub.trial_ends_at) ? 'text-orange-400 font-medium' : ''}>
                          {formatDate(sub.trial_ends_at)}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1">
                        {sub.plan !== 'premium' && (
                          <button onClick={() => handleUpgrade(sub.team_id)} disabled={subActionLoading === sub.team_id}
                            className="p-2 text-green-600 hover:bg-green-500/10 rounded-lg disabled:opacity-50" title="Upgrade">
                            <ArrowUp className="w-4 h-4" />
                          </button>
                        )}
                        {sub.plan !== 'free' && (
                          <button onClick={() => handleDowngrade(sub.team_id)} disabled={subActionLoading === sub.team_id}
                            className="p-2 text-orange-400 hover:bg-orange-500/10 rounded-lg disabled:opacity-50" title="Downgrade">
                            <ArrowDown className="w-4 h-4" />
                          </button>
                        )}
                        {(sub.plan === 'trial' || sub.plan === 'free') && (
                          <button onClick={() => { setSelectedTeam(sub); setShowExtendModal(true) }} disabled={subActionLoading === sub.team_id}
                            className="p-2 text-green-500 hover:bg-indigo-50 rounded-lg disabled:opacity-50" title="Extend Trial">
                            <Plus className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {subsData?.pages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t">
                <p className="text-sm text-zinc-400">Page {subPage} of {subsData.pages}</p>
                <div className="flex gap-2">
                  <button onClick={() => setSubPage(p => Math.max(1, p - 1))} disabled={subPage === 1} className="p-2 border rounded-lg disabled:opacity-50">
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button onClick={() => setSubPage(p => Math.min(subsData.pages, p + 1))} disabled={subPage === subsData.pages} className="p-2 border rounded-lg disabled:opacity-50">
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Extend Trial Modal */}
      {showExtendModal && selectedTeam && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-zinc-800 rounded-2xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Extend Trial</h3>
              <button onClick={() => { setShowExtendModal(false); setSelectedTeam(null) }} className="p-1 hover:bg-zinc-800 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-zinc-300 mb-4">Extend trial for <strong>{selectedTeam.name}</strong></p>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Days to Add</label>
              <input
                type="number"
                value={extendDays}
                onChange={(e) => setExtendDays(parseInt(e.target.value) || 0)}
                min="1"
                max="365"
                className="w-full px-4 py-2 border rounded-lg"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => { setShowExtendModal(false); setSelectedTeam(null) }}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-zinc-900"
              >
                Cancel
              </button>
              <button
                onClick={handleExtendTrial}
                disabled={subActionLoading === selectedTeam?.team_id}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {subActionLoading === selectedTeam?.team_id ? 'Extending...' : 'Extend Trial'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
